/**
 * Phase 46 (Plan 46-07) — Shared 7-kind × 7-channel routing matrix.
 *
 * Consumed by BOTH:
 *   - Plan 46-07 GlobalNotificationRoutesPage (`scope="global"`)
 *   - Plan 46-08 ProjectNotificationsTab on ProjectDetailPage (`scope="project"`)
 *
 * Per UI-SPEC §Surface 1 lines 392-417:
 *   - 7 rows (one per event kind) × 7 columns (one per channel).
 *   - Row header — event_kind human label + small `?` tooltip with hint.
 *   - Column headers — channel human label (11px uppercase tracking-wider).
 *   - Empty cell → em-dash `—` (text-ms-text-muted); clicking calls onAdd.
 *   - Configured no-target cell → green dot + "Active" label + hover affordances.
 *   - Configured target cell → `font-mono` target truncated to ~16 chars +
 *     `title` attr with full value + hover affordances.
 *   - When every cell in a row is empty AND scope='project', show a small
 *     italic "Falls back to global" note next to the kind label.
 *   - Below the matrix: `[+ Add route]` button opens an empty editor modal.
 *
 * Mutation handlers (onAdd / onEdit / onRemove) are passed by the wrapping
 * page — the matrix is purely presentational + click-router.
 */
import { useMemo } from 'react';
import { TrashIcon, PencilIcon } from '../ui/icons';
import { Btn } from '../ui/Btn';
import {
  EVENT_KINDS,
  CHANNELS,
  EVENT_KIND_LABELS,
  EVENT_KIND_TOOLTIPS,
  CHANNEL_LABELS,
  CHANNEL_TOOLTIPS,
  type EventKind,
  type Channel,
} from '../../constants/notificationEnums';

// ---------------------------------------------------------------------------
// Shared route shape — accepts both project- and global-scoped DTOs. The
// matrix only cares about the (eventKind, channel, target, id) tuple plus an
// optional projectId for project-scoped rows.
// ---------------------------------------------------------------------------

export interface MatrixRoute {
  id: number;
  eventKind: EventKind;
  channel: Channel;
  target: string | null;
  projectId?: number;
}

export type MatrixScope = 'project' | 'global';

export interface NotificationRoutingMatrixProps {
  routes: MatrixRoute[];
  scope: 'project' | 'global';
  onAdd: (kind: EventKind, channel: Channel | null) => void;
  onEdit: (route: MatrixRoute) => void;
  onRemove: (route: MatrixRoute) => void;
  readOnly?: boolean;
}

// Build a lookup map indexed by `${eventKind}:${channel}` so each cell lookup
// stays O(1) inside the render loop.
function indexRoutes(routes: MatrixRoute[]): Map<string, MatrixRoute> {
  const map = new Map<string, MatrixRoute>();
  for (const r of routes) {
    map.set(`${r.eventKind}:${r.channel}`, r);
  }
  return map;
}

function truncateTarget(target: string, max = 16): string {
  if (target.length <= max) return target;
  return `${target.slice(0, max - 1)}…`;
}

export function NotificationRoutingMatrix({
  routes,
  scope,
  onAdd,
  onEdit,
  onRemove,
  readOnly = false,
}: NotificationRoutingMatrixProps) {
  const byCell = useMemo(() => indexRoutes(routes), [routes]);

  return (
    <div data-testid="notification-routing-matrix" data-scope={scope}>
      <div className="overflow-x-auto">
        <table className="min-w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-ms-text-muted">
              <th className="py-2 pr-3 font-semibold sticky left-0 bg-ms-surface">
                Event kind
              </th>
              {CHANNELS.map((ch) => (
                <th
                  key={ch}
                  className="py-2 px-3 font-semibold"
                  title={CHANNEL_TOOLTIPS[ch]}
                >
                  {CHANNEL_LABELS[ch].label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ms-border-subtle">
            {EVENT_KINDS.map((kind) => {
              const rowRoutes = CHANNELS.map((ch) =>
                byCell.get(`${kind}:${ch}`),
              );
              const allEmpty = rowRoutes.every((r) => !r);
              const showFallbackNote = allEmpty && scope === 'project';
              return (
                <tr key={kind} data-testid={`matrix-row-${kind}`}>
                  <td className="py-2 pr-3 align-top sticky left-0 bg-ms-surface">
                    <div className="flex items-center gap-1">
                      <span
                        className="font-medium text-ms-text-strong"
                        title={EVENT_KIND_TOOLTIPS[kind]}
                      >
                        {EVENT_KIND_LABELS[kind]}
                      </span>
                      {showFallbackNote && (
                        <span className="text-[11px] text-ms-text-muted italic ml-2">
                          Falls back to global
                        </span>
                      )}
                      {!readOnly && (
                        <Btn
                          size="sm"
                          variant="outline-accent"
                          className="ml-2"
                          onClick={() => onAdd(kind, null)}
                          aria-label={`Add channel for ${EVENT_KIND_LABELS[kind]}`}
                          data-testid={`matrix-row-add-${kind}`}
                        >
                          + Add channel
                        </Btn>
                      )}
                    </div>
                  </td>
                  {CHANNELS.map((ch) => {
                    const route = byCell.get(`${kind}:${ch}`);
                    const cellTestId = `matrix-cell-${kind}-${ch}`;
                    if (!route) {
                      return (
                        <td
                          key={ch}
                          data-testid={cellTestId}
                          data-state="empty"
                          className="py-2 px-3"
                        >
                          {readOnly ? (
                            <span className="text-ms-text-muted">—</span>
                          ) : (
                            <button
                              type="button"
                              className="text-ms-text-muted hover:text-ms-text-strong"
                              onClick={() => onAdd(kind, ch)}
                              aria-label={`Add route for ${EVENT_KIND_LABELS[kind]} on ${CHANNEL_LABELS[ch].label}`}
                            >
                              —
                            </button>
                          )}
                        </td>
                      );
                    }
                    const targetMode = CHANNEL_LABELS[ch].target;
                    return (
                      <td
                        key={ch}
                        data-testid={cellTestId}
                        data-state="configured"
                        className="py-2 px-3 group"
                      >
                        <div className="flex items-center gap-2">
                          {targetMode === 'required' && route.target ? (
                            <span
                              className="font-mono text-[11px] text-ms-text-strong truncate inline-block max-w-[14ch]"
                              title={route.target}
                            >
                              {truncateTarget(route.target)}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[11px] text-emerald-700">
                              <span
                                aria-hidden="true"
                                className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"
                              />
                              Active
                            </span>
                          )}
                          {!readOnly && (
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => onEdit(route)}
                                title="Edit channel target"
                                aria-label={`Edit route for ${EVENT_KIND_LABELS[kind]} on ${CHANNEL_LABELS[ch].label}`}
                                className="text-ms-text-muted hover:text-ms-text-strong"
                                data-testid={`matrix-edit-${kind}-${ch}`}
                              >
                                <PencilIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => onRemove(route)}
                                title="Remove this route"
                                aria-label={`Remove route for ${EVENT_KIND_LABELS[kind]} on ${CHANNEL_LABELS[ch].label}`}
                                className="text-ms-text-muted hover:text-rose-600"
                                data-testid={`matrix-remove-${kind}-${ch}`}
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div className="mt-4">
          <Btn
            variant="outline-accent"
            size="sm"
            onClick={() => onAdd(EVENT_KINDS[0], null)}
            data-testid="matrix-add-route"
          >
            + Add route
          </Btn>
        </div>
      )}
    </div>
  );
}
